import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Wrench, Eye, PlayCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

type SetupPreview = {
  enabledPluginIds: string[];
  manageablePluginIds: string[];
  summary: {
    pluginsWithRequirements: number;
    requiredChannels: number;
    requiredRoles: number;
  };
  requiredChannels: Array<{
    pluginId: string;
    configKey: string;
    channelName: string;
    channelType: string;
  }>;
  requiredRoles: Array<{
    pluginId: string;
    configKey: string;
    roleName: string;
  }>;
};

type SetupReport = {
  dryRun: boolean;
  createdChannels: Array<{ pluginId: string; configKey: string; channelId: string; channelName: string }>;
  createdRoles: Array<{ pluginId: string; configKey: string; roleId: string; roleName: string }>;
  reusedChannels: Array<{ pluginId: string; configKey: string; channelId: string; channelName: string }>;
  reusedRoles: Array<{ pluginId: string; configKey: string; roleId: string; roleName: string }>;
  updatedPlugins: string[];
  enabledPlugins: string[];
  skippedPlugins: string[];
};

export function SetupAssistantPlugin({ serverId }: { serverId: string }) {
  const { data: plugins = [] } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const setupPlugin = (plugins as any[]).find((plugin) => plugin.pluginId === 'setup-assistant');
  const setupEnabled = setupPlugin?.enabled ?? false;

  const {
    data: preview,
    isFetching: loadingPreview,
    refetch: refreshPreview,
  } = useQuery({
    queryKey: ['servers', serverId, 'setup-assistant', 'preview'],
    queryFn: () => pluginApi.getSetupAssistantPreview(serverId) as Promise<SetupPreview>,
  });

  const {
    mutate: toggleSetupPlugin,
    isPending: togglingSetupPlugin,
  } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled
        ? pluginApi.enablePlugin(serverId, 'setup-assistant')
        : pluginApi.disablePlugin(serverId, 'setup-assistant'),
    {
      invalidateQueries: [['servers', serverId, 'plugins']],
      onSuccess: () => toast.success('Plugin setup aggiornato'),
    }
  );

  const {
    mutate: runSetup,
    data: runResult,
    isPending: runningSetup,
  } = useApiMutation(
    ({ dryRun }: { dryRun: boolean }) =>
      pluginApi.applySetupAssistant(serverId, dryRun, preview?.manageablePluginIds || []) as Promise<SetupReport>,
    {
      onSuccess: (result) => {
        if (result.dryRun) {
          toast.success('Simulazione completata');
        } else {
          toast.success('Setup completato');
        }
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore durante il setup');
      },
      invalidateQueries: [
        ['servers', serverId, 'plugins'],
        ['servers', serverId, 'setup-assistant', 'preview'],
      ],
    }
  );

  const currentReport = useMemo(() => runResult as SetupReport | undefined, [runResult]);

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary/20 rounded-xl">
            <Wrench className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Setup Assistant</h3>
            <p className="text-sm text-muted-foreground">
              Crea automaticamente ruoli e canali mancanti per i plugin attivi.
            </p>
          </div>
        </div>
        <Switch
          checked={setupEnabled}
          disabled={togglingSetupPlugin}
          onCheckedChange={(enabled) => toggleSetupPlugin({ enabled })}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1) Analisi plugin attivi</CardTitle>
          <CardDescription>
            Controlla quali plugin attivi hanno requisiti gestibili dal setup automatico.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Plugin attivi</p>
              <p className="text-2xl font-semibold">{preview?.enabledPluginIds?.length ?? 0}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Plugin con requisiti</p>
              <p className="text-2xl font-semibold">{preview?.summary?.pluginsWithRequirements ?? 0}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Canali + ruoli richiesti</p>
              <p className="text-2xl font-semibold">
                {(preview?.summary?.requiredChannels ?? 0) + (preview?.summary?.requiredRoles ?? 0)}
              </p>
            </div>
          </div>

          <Button variant="outline" onClick={() => refreshPreview()} disabled={loadingPreview}>
            {loadingPreview ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analisi in corso...
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Aggiorna analisi
              </>
            )}
          </Button>

          <div className="space-y-3">
            <p className="text-sm font-medium">Plugin gestibili ora:</p>
            <div className="flex flex-wrap gap-2">
              {(preview?.manageablePluginIds || []).map((pluginId) => (
                <Badge key={pluginId} variant="secondary">{pluginId}</Badge>
              ))}
              {(preview?.manageablePluginIds || []).length === 0 && (
                <p className="text-sm text-muted-foreground">Nessun plugin attivo con requisiti automatici.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2) Esecuzione</CardTitle>
          <CardDescription>
            Prima simula senza modifiche, poi applica la creazione reale di canali/ruoli mancanti.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {!setupEnabled && (
            <p className="text-sm text-muted-foreground w-full">
              Attiva prima il plugin Setup Assistant per eseguire simulazione o applicazione.
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => runSetup({ dryRun: true })}
            disabled={runningSetup || !setupEnabled}
          >
            {runningSetup ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Simulazione...
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Simula setup
              </>
            )}
          </Button>

          <Button
            onClick={() => runSetup({ dryRun: false })}
            disabled={runningSetup || !setupEnabled}
          >
            {runningSetup ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creazione...
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4 mr-2" />
                Applica setup
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {currentReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              3) Report {currentReport.dryRun ? 'simulazione' : 'applicazione'}
            </CardTitle>
            <CardDescription>
              Riepilogo delle risorse trovate o create automaticamente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Canali creati</p>
                <p className="text-xl font-semibold">{currentReport.createdChannels.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Ruoli creati</p>
                <p className="text-xl font-semibold">{currentReport.createdRoles.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Canali/ruoli riusati</p>
                <p className="text-xl font-semibold">
                  {currentReport.reusedChannels.length + currentReport.reusedRoles.length}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Plugin config aggiornati</p>
                <p className="text-xl font-semibold">{currentReport.updatedPlugins.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Plugin abilitati in dashboard</p>
                <p className="text-xl font-semibold">{currentReport.enabledPlugins.length}</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Canali creati/pianificati</p>
              <div className="text-sm text-muted-foreground space-y-1">
                {currentReport.createdChannels.map((item, index) => (
                  <p key={`${item.pluginId}-${item.configKey}-${index}`}>
                    {item.pluginId}: {item.channelName} ({item.configKey})
                  </p>
                ))}
                {currentReport.createdChannels.length === 0 && <p>Nessuno</p>}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Ruoli creati/pianificati</p>
              <div className="text-sm text-muted-foreground space-y-1">
                {currentReport.createdRoles.map((item, index) => (
                  <p key={`${item.pluginId}-${item.configKey}-${index}`}>
                    {item.pluginId}: {item.roleName} ({item.configKey})
                  </p>
                ))}
                {currentReport.createdRoles.length === 0 && <p>Nessuno</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
